import * as Sentry from "@sentry/browser";
import { atom } from "jotai";

// Sentry performance monitoring docs: https://docs.sentry.io/platforms/javascript/performance/
// More on how spans work in Sentry v8: https://github.com/getsentry/sentry-javascript/blob/develop/docs/v8-new-performance-apis.md

export type Span = Sentry.Span & {
  start: typeof SentryTraceManager.prototype.start;
};

export type TransactionEntry = "TimeToFirstMFELoad" | "UniversalBootstrap";
export const timeToFirstMfeLoadHasCompletedAtom = atom<boolean>(false);

type UniversalBootstrapTrace =
  | "config-tranche"
  | "exception-monitoring-tranche"
  | "prerequisites-tranche"
  | "universal-bootstrap-steps";

export type SpanEntry =
  | "fetch-shell-bootstrap"
  | "shell-bootstrap"
  | "fetch-shell"
  | "shell-mount-fn"
  | UniversalBootstrapTrace
  | "await-global-components"
  | "fetch-portal-entry"
  | "authenticate-user"
  | "fetch-mfe";

export type PerformanceEntry =
  | "app-start"
  | "shell-bootstrap-start"
  | "shell-bootstrap-end"
  | "shell-start"
  | "shell-mount-start"
  | "shell-mount-end";

class SentryTraceManager {
  private spans: Map<SpanEntry | TransactionEntry, Span> = new Map();

  constructor() {
    document.addEventListener("visibilitychange", () => {
      this.cancelAll();
    });

    window.addEventListener("pagehide", () => {
      this.cancelAll();
    });

    window.addEventListener("beforeunload", () => {
      this.cancelAll();
    });
  }

  public startTransaction(
    name: TransactionEntry,
    options?: Omit<Parameters<typeof Sentry.startSpan>[0], "name">
  ): Span {
    return this._start(name, { ...options, forceTransaction: true });
  }

  public start(
    name: SpanEntry,
    options?: Omit<Parameters<typeof Sentry.startSpan>[0], "name">,
    overrideExistingSpan = false
  ): Span {
    return this._start(name, options, overrideExistingSpan);
  }

  private _start(
    name: SpanEntry | TransactionEntry,
    options?: Omit<Parameters<typeof Sentry.startSpan>[0], "name">,
    overrideExistingSpan?: boolean
  ): Span {
    // Return if a span with the same name already exists
    const existingSpan = this.spans.get(name);
    if (existingSpan && !overrideExistingSpan) {
      return existingSpan;
    }

    const span = Sentry.startInactiveSpan({
      ...options,
      name,
    }) as Span;

    // Lets us start new spans with the current span as the parent
    span.start = (
      name: SpanEntry,
      childOptions: Omit<Parameters<typeof Sentry.startSpan>[0], "name">
    ): Span => this.start(name, { parentSpan: span, ...childOptions });

    this.spans.set(name, span);

    return span;
  }

  public get(name: SpanEntry): Span | undefined {
    return this.spans.get(name);
  }

  public getTransaction(name: TransactionEntry): Span | undefined {
    return this.spans.get(name);
  }

  public cancelAll(): void {
    for (const [_, span] of this.spans) {
      span.end = () => undefined;
    }
  }

  public endTraces(...names: (SpanEntry | TransactionEntry)[]) {
    const endTime = Date.now();
    names.forEach((name) => {
      const span = this.spans.get(name);
      span?.end(endTime);
    });
  }

  public endTrace(name: SpanEntry | TransactionEntry) {
    const span = this.spans.get(name);
    span?.end();
  }
}

const traceManager = new SentryTraceManager();

/**
 * Return absolute time for a performance.mark() entry
 */
const getPerformanceStartTime = (entry: PerformanceEntry, idx = 0) =>
  performance.timeOrigin +
  (performance.getEntriesByName(entry)?.[idx]?.startTime ?? 0);

/**
 * Create traces from when before Sentry was initialized
 */
const setupInitialTraces = (transaction: Span) => {
  let trace = transaction.start("fetch-shell-bootstrap", {
    startTime: getPerformanceStartTime("app-start"),
  });
  trace.end(getPerformanceStartTime("shell-bootstrap-start"));

  trace = transaction.start("shell-bootstrap", {
    startTime: getPerformanceStartTime("shell-bootstrap-start"),
  });
  trace.end(getPerformanceStartTime("shell-bootstrap-end"));

  trace = transaction.start("fetch-shell", {
    startTime: getPerformanceStartTime("shell-bootstrap-end"),
  });
  trace.end(getPerformanceStartTime("shell-start"));

  trace = transaction.start("shell-mount-fn", {
    startTime: getPerformanceStartTime("shell-mount-start"),
  });
  trace.end(getPerformanceStartTime("shell-mount-end"));
};

export type { SentryTraceManager };
export { traceManager, getPerformanceStartTime, setupInitialTraces };
